import { Box, Chip, Stack, Tooltip, Typography, type Theme, alpha } from "@mui/material";
import { ReactElement } from "react";

import { KEEP_SHORTCUT_LABEL, OfferedValue, shortcutLabel } from "./offeredValueShortcuts";

/**
 * What every other source holds for a field, one keystroke away.
 *
 * Rendered in the field's own box, under the input, and not gated on focus: a row
 * that appeared and vanished with the caret would relayout the form on every tab,
 * and a reviewer cannot act on help they have to visit each of sixty fields to
 * discover.
 *
 * Each offer is a real button, labelled and announced — unlike the hover tooltip
 * this replaces, whose contents no keyboard could reach. Taking one is an
 * ordinary edit: the reviewer chose the value, so it is theirs and is recorded as
 * reviewer-entered rather than as something a document said.
 *
 * The chips are deliberately out of the tab order. A review sheet is dozens of
 * inputs with one to three chips under most of them, so leaving them tabbable
 * put two to four stops between every pair of fields on a screen whose whole job
 * is typing — while the keyboard route to them, `⌥↵` and `⌥K`, acts from inside
 * the field and never needed the caret to travel. It also made answering a field
 * by tabbing off it throw the caret away: the Keep chip took focus a moment
 * before the answer removed it, and focus fell back to the document.
 */
/**
 * The offer chips' own shape: squared off, lightly filled.
 *
 * A deliberate, single-place departure from the chip spec, which says only
 * colours change and everything else stays at MUI's defaults. MUI's default is a
 * pill, and a row of pills under an input read as status — labels describing the
 * field — rather than as values to take. Squaring them and filling them makes
 * them read as what they are: buttons carrying a value. The fill itself is in
 * spec ("outlined / light-filled-at-8%"); only the radius is new.
 *
 * Kept here rather than sprinkled as `sx` at the call sites so the deviation is
 * one line to find and one line to undo.
 */
const OFFER_CHIP = (theme: Theme) => ({
  borderRadius: 1.5,
  border: "none",
  // The spec's light-filled chip: info at 8%, not `info.main`. Passing
  // `color="info"` without a variant fell back to MUI's filled default, which
  // paints the palette colour solid — a dark blue behind label text coloured for
  // a light ground, so neither the value nor the keystroke badge could be read.
  // Every colour is set here rather than left to the palette prop, so the chip
  // cannot be repainted by a variant default again.
  bgcolor: alpha(theme.palette.info.main, 0.08),
  color: theme.palette.text.primary,
  "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.16) },
  // Lets a long value give way to its keystroke badge in a narrow column.
  minWidth: 0,
});

/**
 * The keystroke badge beside a chip, drawn as a key cap.
 *
 * Beside rather than inside, and always laid out. Only the focused field's
 * badges are painted, and hiding them outright resized every chip on the row as
 * the caret arrived and left — a form that reflowed under the reviewer on every
 * tab. `visibility` keeps the box, so nothing moves; sitting outside the chip
 * keeps the chip's own fill over its text rather than over an empty reservation.
 */
const SHORTCUT_BADGE = (theme: Theme) => ({
  fontSize: "0.65rem",
  color: theme.palette.text.secondary,
  bgcolor: theme.palette.background.paper,
  border: 1,
  borderColor: theme.palette.divider,
  borderRadius: 0.5,
  px: 0.4,
  flexShrink: 0,
});

/**
 * What keeping does, said in the same breath as where the other values come
 * from.
 *
 * Every offer names its source on hover; without this the one chip that is not
 * an offer was the only one that explained nothing — and it is the one whose
 * effect is least obvious, because nothing about the field changes when it is
 * pressed. Any extra the caller has (a confidence score) follows.
 */
function keepExplanation(extra: string | undefined): string {
  const what = "Keep what this field holds and stop flagging it. Nothing is changed.";
  return extra == null ? what : `${what} ${extra}`;
}

/** Wraps a chip in a tooltip only when there is something to say. */
function withTooltip(chip: ReactElement, tooltip: string | undefined): ReactElement {
  if (tooltip == null) return chip;
  return (
    <Tooltip title={tooltip} placement="top">
      {chip}
    </Tooltip>
  );
}

/**
 * A chip and the keystroke that presses it, as one unit.
 *
 * The badge is a sibling rather than part of the label so the chip's fill stops
 * at its own text. Its box is always here — `REVEAL_SHORTCUTS_*` paints it or
 * not — which is what stops the row resizing as the caret moves between fields.
 *
 * Capped at the offers row, so the chip's own `max-width: 100%` measures the
 * field's column rather than this wrapper and a long value ellipsizes instead of
 * running into the next field.
 */
function OfferChip({ chip, shortcut }: { chip: ReactElement; shortcut: string }): ReactElement {
  return (
    <Stack direction="row" gap={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
      {chip}
      <Box component="span" aria-hidden data-offer-shortcut sx={SHORTCUT_BADGE}>
        {shortcut}
      </Box>
    </Stack>
  );
}

export function OfferedValues<T>({
  offers,
  onTake,
  onKeep,
  keepValue,
  keepTooltip,
  ariaLabel = "Values from other sources",
}: {
  offers: readonly OfferedValue<T>[];
  onTake: (offer: OfferedValue<T>) => void;
  /**
   * Answer the question by keeping what the field already holds.
   *
   * Not an edit: the value does not change. The reviewer has looked at both and
   * said which one they want, so the warning, the count and travel stop asking —
   * without recording a correction nobody made, which would tell the extraction
   * pipeline a value was fixed when it was confirmed.
   *
   * Omit it where there is no flag to clear and nothing is asking.
   */
  onKeep?: () => void;
  /**
   * What the field holds now, in display form, so keeping it names the value
   * rather than a mood. "Keep 141" beside "Saved: 200" is one row of
   * values a reviewer picks from; "Reads right" was a different kind of sentence
   * that made the reader stop and work out which it applied to.
   */
  keepValue?: string;
  /**
   * Appended to the keep chip's own explanation, when there is more to say —
   * the extractor's confidence, on a value it still holds. Set it only while
   * that is true: once the reviewer has typed over the reading, a score from the
   * extractor describes a value that is no longer there.
   */
  keepTooltip?: string;
  ariaLabel?: string;
}) {
  if (offers.length === 0 && onKeep == null) return null;

  return (
    <Stack
      direction="row"
      role="group"
      aria-label={ariaLabel}
      // Wider than the gap inside a pair, so a badge reads as belonging to the
      // chip on its left rather than the one on its right.
      gap={1.25}
      sx={{ flexWrap: "wrap", mt: 0.5 }}
    >
      {offers.map((offer, index) => (
        <OfferChip
          key={`${offer.source}:${offer.display}`}
          shortcut={shortcutLabel(index)}
          chip={withTooltip(
            <Chip
              size="small"
              sx={OFFER_CHIP}
              // Out of the tab order: `⌥↵` / `⌥{n}` act from inside the field, and
              // a stop per chip between every pair of inputs is what a
              // transcription pass cannot afford.
              tabIndex={-1}
              onClick={() => onTake(offer)}
              // Source, value, then what pressing it does. Named in this order because
              // it reads correctly whatever the source is called — "e-Manifest:
              // 21,440 lb. Use this value." and "Record: 200. Use this value." — where
              // a template that folds the source into a sentence only reads well for
              // one of them.
              aria-label={`${offer.source}: ${offer.display}. Use this value.`}
              label={
                <Stack direction="row" gap={0.5} alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    {offer.source}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="text.primary">
                    {offer.display}
                  </Typography>
                </Stack>
              }
            />,
            offer.tooltip
          )}
        />
      ))}
      {onKeep != null && (
        <OfferChip
          shortcut={KEEP_SHORTCUT_LABEL}
          chip={withTooltip(
            // A sibling of the offers, not a different kind of control: same chip,
            // same two-part label, the value in the same weight. All of them answer
            // the same question — which value does this field end up with — and one
            // of the answers happens to be the one already there.
            <Chip
              size="small"
              sx={OFFER_CHIP}
              tabIndex={-1}
              onClick={onKeep}
              aria-label={`Keep ${keepValue ?? "this value"}`}
              label={
                <Stack direction="row" gap={0.5} alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    Keep
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="text.primary">
                    {keepValue}
                  </Typography>
                </Stack>
              }
            />,
            keepExplanation(keepTooltip)
          )}
        />
      )}
    </Stack>
  );
}
